import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store";
import { toast } from "sonner";
import axios from "axios";
import { Search, LogOut, ArrowRight, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const AdminNavbar = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <img src="/assets/logo-creavideos.png" alt="CreaVideos" className="h-8 w-auto" />
            </Link>
            <span className="px-2 py-1 rounded bg-teal-600 text-xs font-semibold">ADMIN</span>
          </div>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/admin" className="text-white/80 hover:text-white transition-colors font-medium">Dashboard</Link>
            <Link to="/admin/templates" className="text-white/80 hover:text-white transition-colors font-medium">Templates</Link>
            <Link to="/admin/users" className="text-white/80 hover:text-white transition-colors font-medium">Users</Link>
            <Link to="/admin/user-stats" className="text-white font-medium">User Stats</Link>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-2 rounded-full hover:bg-white/10">
                <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center">
                  <span className="text-sm font-medium">{user?.name?.[0]}</span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/dashboard" className="cursor-pointer">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  User Dashboard
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="cursor-pointer text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
};

export default function AdminUserStats() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        const response = await axios.get("/admin/user-stats");
        setUsers(response.data || []);
      } catch (error) {
        console.error("Failed to fetch user stats:", error);
        toast.error("Failed to load user stats");
      } finally {
        setLoading(false);
      }
    };
    fetchUserStats();
  }, []);

  const filteredUsers = useMemo(() => users.filter((user) => (
    user.name?.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase())
  )), [search, users]);

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminNavbar />

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-1">User Usage Stats</h1>
            <p className="text-slate-600">Monitor exports and AI image-to-video generations</p>
          </div>

          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 bg-white"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">Loading...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Exports (Month/Total)</TableHead>
                    <TableHead>AI Gen (Month/Total)</TableHead>
                    <TableHead>Templates used</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" />{user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{user.subscription_plan || "Free"}</TableCell>
                      <TableCell>{user.exports_current_month || 0} / {user.exports_total || 0}</TableCell>
                      <TableCell>{user.ai_generations_current_month || 0} / {user.ai_generations_total || 0}</TableCell>
                      <TableCell>
                        {user.templates_used_current_month?.length ? (
                          <div className="flex flex-wrap gap-2">
                            {user.templates_used_current_month.map((templateUsage) => {
                              const templateName = typeof templateUsage === "string" ? templateUsage : templateUsage.name;
                              const exportCount = typeof templateUsage === "string" ? null : templateUsage.count;

                              return (
                                <span
                                  key={templateName}
                                  className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700"
                                >
                                  {templateName}{exportCount !== null ? `: ${exportCount}` : ""}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
